import { Box, Button, Stack, Typography } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import FormField from './FormField';
import { COLORS } from './formTheme';

// [step index, section title key, [field name, label key][]]
const SECTIONS = [
  [0, 'section1_title', [
    ['general.certificateName', 'certificate_name'],
    ['general.fullName', 'full_name'],
    ['general.preferredName', 'preferred_name'],
    ['general.instagramProfile', 'instagram_profile'],
    ['general.email', 'email'],
    ['general.shirtSize', 'shirt_size'],
    ['general.emergencyContact', 'emergency_contact'],
    ['general.age', 'age'],
    ['general.attendedPreviousEdition', 'attended_previous_edition'],
    ['general.travelMethod', 'travel_method'],
    ['general.useWhatsApp', 'use_whatsapp'],
    ['general.allowGroupContact', 'allow_group_contact'],
    ['general.imageConsent', 'image_consent'],
  ]],
  [1, 'section2_title', [
    ['accommodationPreferences.diet', 'diet'],
    ['accommodationPreferences.dietOther', 'diet'],
    ['accommodationPreferences.dairyProducts', 'dairy_products'],
    ['accommodationPreferences.accommodationPreferences', 'accommodation_preferences'],
    ['accommodationPreferences.healthSafetyInfo', 'health_safety_info'],
  ]],
  [2, 'section3_title', [
    ['eventActivities.saunaConsent', 'sauna_consent'],
    ['eventActivities.yogaInterest', 'yoga_interest'],
    ['eventActivities.hikingInterest', 'hiking_interest'],
    ['eventActivities.hikingAlternative', 'hiking_alternative'],
    ['eventActivities.hookRitualInterest', 'hook_ritual_interest'],
    ['eventActivities.playPiercingInterest', 'play_piercing_interest'],
    ['eventActivities.piercersForPiercers', 'piercers_for_piercers'],
    ['eventActivities.piercersForPiercersDescription', 'piercers_for_piercers'],
    ['eventActivities.shibariWorkshopInterest', 'shibari_workshop_interest'],
    ['eventActivities.shibariSkillLevel', 'shibari_skill_level'],
    ['eventActivities.shibariLectureInterest', 'shibari_lecture_interest'],
    ['eventActivities.shibariLectureInterestDesc', 'shibari_lecture_interest'],
  ]],
  [3, 'section4_title', [
    ['bodySuspension.suspensionInterest', 'suspension_interest'],
    ['bodySuspension.firstTimeSuspension', 'first_time_suspension'],
    ['bodySuspension.suspensionPosition', 'suspension_position'],
    ['bodySuspension.jointSuspension', 'joint_suspension'],
    ['bodySuspension.jointSuspensionPartner', 'joint_suspension_partner'],
    ['bodySuspension.privacyPreference', 'privacy_preference'],
    ['bodySuspension.suspensionIntention', 'suspension_intention'],
    ['bodySuspension.suspensionVision', 'suspension_vision'],
    ['bodySuspension.suspensionHealthInfo', 'suspension_health_info'],
  ]],
  [4, 'section5_title', [
    ['paymentDetails.paymentName', 'final_first_name'],
    ['paymentDetails.paymentSurname', 'final_last_name'],
    ['paymentDetails.tel', 'final_phone'],
    ['paymentDetails.countryCodePayment', 'final_country'],
    ['paymentDetails.addressLine1', 'final_address'],
    ['paymentDetails.locality', 'final_city'],
    ['paymentDetails.postalCode', 'final_postal_code'],
  ]],
];

// Free-text "other"/description fields are shown under their parent question.
const FOLLOW_UP = new Set([
  'accommodationPreferences.dietOther',
  'eventActivities.piercersForPiercersDescription',
  'eventActivities.shibariLectureInterestDesc',
]);

const short = (text, max = 90) => {
  const flat = String(text).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max)}…` : flat;
};

export default function ReviewForm({ goToStep }) {
  const { t } = useTranslation();
  const { lang } = useParams();
  const {
    getValues,
    register,
    formState: { errors },
  } = useFormContext();
  const values = getValues();

  const display = (name, raw) => {
    if (raw === 'TAK') return t('yes');
    if (raw === 'NIE') return t('no');
    if (raw === 'public') return t('public');
    if (raw === 'private') return t('private');
    if (name === 'paymentDetails.tel') {
      return `${values.paymentDetails?.countryCode ?? ''} ${raw}`.trim();
    }
    return raw;
  };

  const get = (name) => name.split('.').reduce((o, k) => o?.[k], values);

  return (
    <Stack spacing={3}>
      <Typography sx={{ color: COLORS.muted }}>{t('review_intro')}</Typography>

      {SECTIONS.map(([step, titleKey, fields]) => {
        const rows = fields.filter(([name]) => get(name));
        return (
          <Box
            key={titleKey}
            sx={{
              border: `1px solid ${COLORS.border}`,
              borderRadius: 1.5,
              backgroundColor: COLORS.card,
              p: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>{t(titleKey)}</Typography>
              <Button
                type='button'
                size='small'
                onClick={() => goToStep?.(step)}
                sx={{ color: COLORS.accent, textDecoration: 'underline', textTransform: 'none' }}
              >
                {t('edit')}
              </Button>
            </Box>
            {rows.length === 0 && (
              <Typography sx={{ color: COLORS.muted }}>—</Typography>
            )}
            {rows.map(([name, labelKey]) => (
              <Box
                key={name}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '40% 1fr' },
                  gap: { xs: 0, sm: 2 },
                  py: 0.75,
                  borderTop: `1px solid ${COLORS.border}`,
                  '&:first-of-type': { borderTop: 0 },
                }}
              >
                <Typography sx={{ color: COLORS.muted, fontSize: '0.875rem' }}>
                  {FOLLOW_UP.has(name) ? '↳' : short(t(labelKey))}
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {display(name, get(name))}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}

      <FormField
        type='checkbox'
        label={
          <>
            {t('accept_prefix')}{' '}
            <Link to={`/${lang}/terms/tcs`} target='_blank'>
              {t('terms_link')}
            </Link>{' '}
            {t('and')}{' '}
            <Link to={`/${lang}/terms/privacy`} target='_blank'>
              {t('privacy_link')}
            </Link>
            .
          </>
        }
        name='paymentDetails.acceptTerms'
        register={register('paymentDetails.acceptTerms')}
        error={errors?.paymentDetails?.acceptTerms}
        helperText={errors?.paymentDetails?.acceptTerms?.message}
        fullWidth
      />
    </Stack>
  );
}
